/**
 * User Status Monitor Hook
 * 
 * This hook monitors the current user's status and existence in the system.
 * If the user is deactivated or deleted, it automatically logs them out.
 * 
 * Usage:
 * useUserStatusMonitor(); // Call this in App.jsx or Layout component
 */

import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { handleSignOut } from '../services/amplify/helpers';
import { clearUser } from '../store/slice/authSlice';
import toast from 'react-hot-toast';

const CHECK_INTERVAL = 15000; // Check every 15 seconds

export const useUserStatusMonitor = () => {
  const dispatch = useDispatch();
  const isLogged = useSelector(state => state.user.isLogged);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isLogged) {
      // Clear any existing interval when user is logged out
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const checkUserStatus = async () => {
      try {
        // First, verify session is still valid and get fresh token
        const session = await fetchAuthSession({ forceRefresh: true });
        const userId = session.tokens?.idToken?.payload?.sub;

        if (!userId) {
          await performLogout('Session invalid. Please log in again.');
          return;
        }

        // Check if user is enabled in Cognito
        // When a user is disabled via adminDisableUser, this will be reflected in the token
        const userEnabled = session.tokens?.idToken?.payload['cognito:user_status'];
        
        // If user status shows they're disabled/deactivated
        if (userEnabled && userEnabled !== 'CONFIRMED') {
          await performLogout('Your account has been deactivated. Please contact an administrator.');
          return;
        }

        // Fetch user attributes to check custom status
        const attributes = await fetchUserAttributes();
        const customStatus = attributes['custom:status'];

        // If custom status is explicitly set to inactive, log them out
        if (customStatus && customStatus === 'inactive') {
          await performLogout('Your account has been deactivated. Please contact an administrator.');
          return;
        }

      } catch (error) {
        console.error('Error checking user status:', error);
        
        // Handle specific errors that indicate user should be logged out
        const errorMessage = error.message || error.toString();
        const errorName = error.name || '';
        
        // User is disabled in Cognito
        if (errorMessage.includes('User is disabled') || 
            errorMessage.includes('user is disabled') ||
            errorName === 'UserDisabledException') {
          await performLogout('Your account has been deactivated. Please contact an administrator.');
          return;
        }
        
        // User not found or not authenticated
        if (errorName === 'UserNotFoundException' || 
            errorMessage.includes('not authenticated') ||
            errorMessage.includes('No current user')) {
          await performLogout('Your account is no longer valid. Please log in again.');
          return;
        }
        
        // For other errors (network issues, etc.), don't logout - let user continue
      }
    };

    const performLogout = async (message) => {
      // Clear interval first
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // If manual logout is in progress, skip forced logout actions
      if (window.isManualLogout) {
        window.isManualLogout = false;
        return;
      }

      // Sign out from Amplify
      await handleSignOut();
      // Clear Redux state
      dispatch(clearUser());
      // Show message to user
      toast.error(message, { duration: 5000 });
    };

    // Initial check
    checkUserStatus();

    // Set up periodic checks
    intervalRef.current = setInterval(checkUserStatus, CHECK_INTERVAL);

    // Also check when user returns to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkUserStatus();
      }
    };

    // Check when user interacts with the page
    const handleUserActivity = () => {
      // Throttle checks - only check if last check was more than 5 seconds ago
      const now = Date.now();
      if (!window.lastUserStatusCheck || now - window.lastUserStatusCheck > 5000) {
        window.lastUserStatusCheck = now;
        checkUserStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('click', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('click', handleUserActivity);
      document.removeEventListener('keydown', handleUserActivity);
    };
  }, [isLogged, dispatch]);
};

export default useUserStatusMonitor;
